import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Download, Search, ChevronRight, ChevronDown, Printer, FileText, FileCode, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1).replace(/0+$/, '').replace(/\.$/, '')}L`;
  return Number(v).toLocaleString('en-IN');
};

function TOCItem({ item, activeSection, onClick }) {
  const [open, setOpen] = useState(true);
  const isActive = activeSection === item.id;
  return (
    <div>
      <button onClick={() => { onClick(item.id); if (item.children) setOpen(!open); }}
        className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] flex items-center gap-1.5 transition-all ${isActive ? 'bg-[#2A7B88]/10 text-[#2A7B88] font-semibold' : 'text-[#6B7280] hover:text-[#1B2A4A] hover:bg-[#F7F5F2]'}`}
        data-testid={`toc-${item.id}`}>
        {item.children ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span className="w-3" />}
        {item.label}
      </button>
      {open && item.children && (
        <div className="ml-3 border-l border-[#E5E1DC] pl-1">
          {item.children.map(child => (
            <button key={child.id} onClick={() => onClick(child.id)}
              className={`w-full text-left px-3 py-1 text-[12px] rounded-md transition-all ${activeSection === child.id ? 'text-[#2A7B88] font-medium' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}>
              {child.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SystemDocs() {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    api.get('/docs/system-guide').then(r => { setDocs(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDownload = (format) => {
    const token = localStorage.getItem('access_token');
    window.open(`${API_URL}/api/docs/download/${format}?token=${token}`, '_blank');
  };

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!docs) return <p className="text-center py-10 text-[#6B7280]">Unable to load documentation.</p>;

  const toc = [
    { id: 'overview', label: 'Overview', children: [
      { id: 'principles', label: 'Core Principles' },
      { id: 'quarters', label: 'Quarter Standard' },
    ]},
    { id: 'group-structure', label: 'Group Structure & Targets' },
    { id: 'me-division', label: 'MindEscapes (ME)', children: [
      { id: 'me-outlets', label: 'Operating Outlets' },
      { id: 'me-b2b', label: 'B2B Club Sales' },
      { id: 'me-cost', label: 'Cost Module' },
      { id: 'me-bookings', label: 'Booking Pipeline' },
      { id: 'me-access', label: 'Access Hierarchy' },
    ]},
    { id: 'cc-division', label: 'Club Concierge (CC)' },
    { id: 'lc-division', label: 'Les Concierges (LC)' },
    { id: 'tdb-division', label: 'The Doggy Bakery (TDB)' },
    { id: 'architecture', label: 'System Architecture', children: [
      { id: 'tech-stack', label: 'Tech Stack' },
      { id: 'file-structure', label: 'File Structure' },
      { id: 'env-vars', label: 'Environment Variables' },
    ]},
    { id: 'database', label: 'Database Schema' },
    { id: 'api-reference', label: 'API Reference' },
    { id: 'data-flows', label: 'Data Flows' },
    { id: 'roles', label: 'User Roles & Access' },
    { id: 'users', label: 'System Users' },
    { id: 'import-formats', label: 'Import Formats' },
    { id: 'admin', label: 'Admin Capabilities' },
    { id: 'training-architecture', label: 'Training Architecture', children: [
      { id: 'training-summary', label: 'Summary' },
      { id: 'training-departments', label: 'ME Departmental Plan' },
      { id: 'training-implementation', label: 'Implementation Plan' },
    ]},
    { id: 'integrations', label: 'Integrations' },
  ];

  const live = docs.live_data || {};
  const arch = docs.architecture || {};
  const filteredCollections = (live.collections || []).filter(c =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex gap-0 -mx-4 sm:-mx-6" data-testid="system-docs-page">
      {/* Sidebar TOC */}
      <aside className="hidden lg:block w-64 shrink-0 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto border-r bg-white p-4 print:hidden"
        style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: '#E5E1DC' }}>
          <BookOpen size={18} style={{ color: '#2A7B88' }} />
          <span className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>System Docs</span>
        </div>
        <div className="space-y-0.5">
          {toc.map(item => <TOCItem key={item.id} item={item} activeSection={activeSection} onClick={scrollTo} />)}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 px-4 sm:px-6 pb-20" ref={contentRef}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 print:hidden">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#1B2A4A' }} data-testid="docs-title">ScaleBoard System Documentation</h1>
            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
              Auto-generated from live database — {new Date(docs.generated_at).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search docs..."
                className="pl-8 pr-3 py-2 text-sm rounded-lg border bg-white" style={{ borderColor: '#E5E1DC', width: '200px' }}
                data-testid="docs-search" />
            </div>
            <button onClick={() => handleDownload('txt')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border hover:bg-[#F7F5F2] transition-all"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="download-txt">
              <FileText size={14} /> TXT
            </button>
            <button onClick={() => handleDownload('html')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border hover:bg-[#F7F5F2] transition-all"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="download-html">
              <FileCode size={14} /> HTML
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-white transition-all"
              style={{ backgroundColor: '#2A7B88' }} data-testid="download-pdf">
              <Printer size={14} /> PDF
            </button>
          </div>
        </div>

        {/* ============ SECTIONS ============ */}
        <div className="space-y-8">

          {/* Complete Architecture Hero Tile — single source of truth for every agent */}
          <div className="rounded-xl border-2 p-5 relative overflow-hidden print:hidden"
               style={{ borderColor: '#2A7B88', backgroundColor: '#F0F8F9' }}
               data-testid="complete-architecture-tile">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
                <FileText size={24} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Complete Architecture Document</h2>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide" style={{ backgroundColor: '#2A7B88', color: 'white' }}>READ FIRST</span>
                </div>
                <p className="text-sm mb-3" style={{ color: '#4B5563' }}>
                  Single source of truth for every agent, developer, and stakeholder. Covers the full database audit,
                  all 583 API endpoints, 25 frontend pages, 13 integrations, 17 cron jobs, Mira's 37 intents,
                  admin capabilities, env vars, and known bugs. Every agent handoff references this file.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="/docs/complete-architecture" target="_blank" rel="noopener"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white hover:opacity-90 transition-all"
                     style={{ backgroundColor: '#2A7B88' }}
                     data-testid="view-complete-architecture-btn">
                    <BookOpen size={13} /> View Full Document
                  </a>
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('access_token');
                      window.open(`${API_URL}/api/docs/complete-architecture/download?token=${token}`, '_blank');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-white transition-all"
                    style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                    data-testid="download-complete-architecture-btn">
                    <Download size={13} /> Download .md
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* OVERVIEW */}
          <Section id="overview" title="1. What is ScaleBoard">
            <p>ScaleBoard is the Leadership Operating System for Les Concierges Group. It provides a single source of truth for revenue, costs, pipeline, people, and operational metrics across all group divisions.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
              {['Are we winning?', "What's the biggest problem?", "What's the biggest opportunity?", 'Who needs my attention?'].map((q, i) => (
                <div key={i} className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
                  <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{q}</p>
                </div>
              ))}
            </div>
            <SubSection id="principles" title="Core Principles">
              <ul className="space-y-2">
                <li><strong>RULE #1: Zero Hardcoding</strong> — Every target, outlet, owner, deal editable via Admin</li>
                <li><strong>RULE #2: One source of truth</strong> — Change once, updates everywhere</li>
                <li><strong>RULE #3: Revenue without cost is vanity</strong> — Every division shows margin</li>
                <li><strong>RULE #4: Measure both sides</strong> — Celebrate wins + flag problems</li>
              </ul>
            </SubSection>
            <SubSection id="quarters" title="Quarter Standard">
              <p>ALL divisions use standard 3-month quarters: Q1 Apr-Jun | Q2 Jul-Sep | Q3 Oct-Dec | Q4 Jan-Mar</p>
            </SubSection>
          </Section>

          {/* GROUP STRUCTURE */}
          <Section id="group-structure" title="2. Group Structure & Targets">
            <p className="mb-3">Total group target FY26-27: <strong>Rs.49.70 Crores</strong> across 5 divisions.</p>
            <Table headers={['Division', 'Leader', 'Annual Target', 'Contracted', 'Gap']} rows={[
              ['CC Loyalty', 'Nisha', 'Rs.6.81Cr', 'Rs.4.81Cr', 'Rs.2Cr'],
              ['CC Residential', 'Samar', 'Rs.2.00Cr', 'Rs.36L', 'Rs.1.64Cr'],
              ['LC', 'Srikanth', 'Rs.9.00Cr', 'Rs.7.50Cr', 'Rs.1.50Cr'],
              ['ME', 'Dhiraj + Nisha', 'Rs.29.64Cr', 'Rs.8.67Cr', 'Rs.5Cr'],
              ['TDB', 'Aditya', 'Rs.2.25Cr', '-', '-'],
            ]} />
            <div className="mt-3">
              <strong>Live Division Data ({(live.divisions || []).length} divisions):</strong>
              <Table headers={['ID', 'Name', 'Leader', 'Entity', 'Revenue Fields', 'Pipeline', 'Contracts']}
                rows={(live.divisions || []).map(d => [
                  d.id, d.name, d.leader_name, d.entity_name, d.revenue_fields, d.has_pipeline ? 'Yes' : 'No', d.has_contracts ? 'Yes' : 'No'
                ])} />
            </div>
          </Section>

          {/* ME */}
          <Section id="me-division" title="3. MindEscapes (ME) — Rs.29.64Cr">
            <p>ME = Operating revenue (Rs.15.97Cr) + B2B Club Sales (Rs.13.67Cr)</p>
            <SubSection id="me-outlets" title="3.1 Operating Outlets (Rs.15.97Cr)">
              <Table headers={['Outlet', 'Owner', 'Annual', 'Q1', 'Status']} rows={[
                ['Celeste (KETTI)', 'Smith R. Paul', 'Rs.6.60Cr', 'Rs.1.65Cr', 'Active'],
                ['Stays By MindEscapes', 'Siddharth', 'Rs.3.28Cr', 'Rs.82L', 'Active'],
                ['ANMA (COONOOR)', 'Angad', 'Rs.1.80Cr', 'Rs.45L', 'Active'],
                ['ANMA (OOTY)', 'Angad', 'Rs.1.20Cr', 'Rs.30L', 'Active'],
                ['XIN HSR Layout', 'Angad', 'Rs.1.20Cr', 'Rs.30L', 'Active'],
                ['XIN Koramangala', 'Angad', 'Rs.1.20Cr', 'Rs.30L', 'Active'],
                ['Club Social B2C', 'Amitabho', 'Rs.69L', 'Rs.15L', 'Active'],
                ['Miracles (Nelamangala)', 'TBC', 'Rs.0', 'Rs.0', 'Coming Soon'],
              ]} />
            </SubSection>
            <SubSection id="me-b2b" title="3.2 B2B Club Sales (Rs.13.67Cr — Nisha)">
              <Table headers={['Deal', 'Annual', 'Status']} rows={[
                ['JLR', 'Rs.5.92Cr', 'Contracted'],
                ['Dreamfolks', 'Rs.2.025Cr', 'Signed'],
                ['AaBee Circle', 'Rs.72.63L', 'Signed'],
                ['New Business', 'Rs.5Cr', 'Pipeline (gap)'],
              ]} />
            </SubSection>
            <SubSection id="me-cost" title="3.3 Cost Module">
              <p>123 ingredients, 51 recipes, 8 kitchens. Historical data Nov 2025-Mar 2026. Net F&B cost improved from 52.3% to 27.3%.</p>
            </SubSection>
            <SubSection id="me-bookings" title="3.4 Booking Pipeline">
              <p>8 stages, 4 views (Kanban/Table/Calendar/Occupancy). Room config: 7 Suites + 3 Deluxe = 10 rooms.</p>
            </SubSection>
            <SubSection id="me-access" title="3.5 Access Hierarchy">
              <ul className="space-y-1 text-sm">
                <li>Dipali (CEO) — sees everything</li>
                <li>Dhiraj (Leader) — all ME including Bookings</li>
                <li>Ronald (Nilgiris sub_leader) — F&B + Accommodation</li>
                <li>Siddharth (sub_leader) — Stays only, auto-redirects to /bookings</li>
                <li>Saket (cost_controller) — all ME cost data</li>
                <li>Nihal (submitter) — daily sales + monthly costs entry</li>
              </ul>
            </SubSection>
          </Section>

          {/* CC */}
          <Section id="cc-division" title="4. Club Concierge (CC) — Rs.8.81Cr">
            <p><strong>CC Loyalty (Nisha):</strong> Xiaomi Rs.3.21Cr + RedBeryl Rs.1.60Cr + New Business Rs.2Cr</p>
            <p><strong>CC Residential (Samar):</strong> Godrej Rs.36L + New Business Rs.1.64Cr. Custom quarterly ramp.</p>
          </Section>

          {/* LC */}
          <Section id="lc-division" title="5. Les Concierges (LC) — Rs.9.00Cr">
            <p>Existing B2B clients Rs.7.50Cr carry forward. New business Rs.1.50Cr (20% growth mandate). Equal quarters Rs.2.25Cr.</p>
          </Section>

          {/* TDB */}
          <Section id="tdb-division" title="6. The Doggy Bakery (TDB) — Rs.2.25Cr">
            <Table headers={['City', 'FY25-26', 'FY26-27 Target', 'Growth']} rows={[
              ['Bengaluru', 'Rs.85.51L', 'Rs.1.10Cr', '29%'],
              ['Mumbai', 'Rs.35.74L', 'Rs.50L', '40%'],
              ['Gurgaon', 'Rs.5.30L', 'Rs.25L', 'Full year'],
              ['Online/B2B', '-', 'Rs.40L', 'New channel'],
            ]} />
            <p className="text-[12px] mt-3" style={{ color: '#6B7280' }}>
              <strong style={{ color: '#1B2A4A' }}>Training Library (Live 23-Apr-2026):</strong> 20 modules across 4 waves — Brand & Mission (01–03) · Kitchen & Product (04–10) · Store & Customer (11–16) · Operations & Culture (17–20). Built on Mira Sikand's 75-year recipe legacy. 340 assignments (17 people × 20 modules). Accessible via Division Detail → Training tab.
            </p>
          </Section>

          {/* ARCHITECTURE */}
          <Section id="architecture" title="7. System Architecture">
            <SubSection id="tech-stack" title="7.1 Tech Stack">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(arch.tech_stack || {}).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{k}</p>
                    <p className="text-sm mt-1" style={{ color: '#1B2A4A' }}>{v}</p>
                  </div>
                ))}
              </div>
            </SubSection>
            <SubSection id="file-structure" title="7.2 File Structure">
              {Object.entries(arch.file_structure || {}).map(([section, files]) => (
                <div key={section} className="mb-4">
                  <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#2A7B88' }}>{section}</h5>
                  <div className="space-y-1">
                    {Object.entries(files).map(([path, desc]) => (
                      <div key={path} className="flex gap-3 text-[12px]">
                        <code className="shrink-0 bg-[#F7F5F2] px-1.5 py-0.5 rounded text-[11px]" style={{ color: '#2A7B88' }}>{path.replace('/app/', '')}</code>
                        <span style={{ color: '#6B7280' }}>{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </SubSection>
            <SubSection id="env-vars" title="7.3 Environment Variables">
              {Object.entries(arch.env_vars || {}).map(([env, vars]) => (
                <div key={env} className="mb-3">
                  <h5 className="text-xs font-bold uppercase mb-1" style={{ color: '#2A7B88' }}>{env}</h5>
                  {Object.entries(vars).map(([k, v]) => (
                    <div key={k} className="flex gap-3 text-[12px] mb-1">
                      <code className="shrink-0 bg-[#F7F5F2] px-1.5 py-0.5 rounded text-[11px]">{k}</code>
                      <span style={{ color: '#6B7280' }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </SubSection>
          </Section>

          {/* DATABASE SCHEMA */}
          <Section id="database" title="8. Database Schema (Live)">
            <p className="mb-3 text-sm" style={{ color: '#6B7280' }}>
              {filteredCollections.length} collections, {filteredCollections.reduce((s, c) => s + c.count, 0).toLocaleString()} total documents
            </p>
            {filteredCollections.map(col => (
              <div key={col.name} className="mb-4 p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-bold" style={{ color: '#2A7B88' }}>{col.name}</code>
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
                    {col.count} docs
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {col.fields.map(f => (
                    <code key={f} className="text-[11px] px-2 py-0.5 rounded bg-[#F7F5F2]" style={{ color: '#1B2A4A' }}>{f}</code>
                  ))}
                </div>
              </div>
            ))}
          </Section>

          {/* API REFERENCE */}
          <Section id="api-reference" title="9. API Reference">
            {(docs.api_reference || []).map((group, gi) => (
              <div key={gi} className="mb-6">
                <h4 className="text-sm font-bold mb-2" style={{ color: '#1B2A4A' }}>{group.group}</h4>
                <div className="space-y-2">
                  {group.endpoints.map((ep, ei) => (
                    <div key={ei} className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          ep.method === 'GET' ? 'bg-emerald-50 text-emerald-700' :
                          ep.method === 'POST' ? 'bg-blue-50 text-blue-700' :
                          ep.method === 'PUT' ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>{ep.method}</span>
                        <code className="text-[12px] font-medium" style={{ color: '#1B2A4A' }}>{ep.path}</code>
                      </div>
                      <p className="text-[12px]" style={{ color: '#6B7280' }}>{ep.desc}</p>
                      {ep.params && <p className="text-[11px] mt-1"><strong>Params:</strong> <code>{ep.params}</code></p>}
                      {ep.body && <p className="text-[11px] mt-0.5"><strong>Body:</strong> <code>{ep.body}</code></p>}
                      <p className="text-[11px] mt-0.5"><strong>Returns:</strong> {ep.returns}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Section>

          {/* DATA FLOWS */}
          <Section id="data-flows" title="10. Data Flows">
            {(docs.data_flows || []).map((flow, fi) => (
              <div key={fi} className="mb-5 p-4 rounded-lg border" style={{ borderColor: '#E5E1DC', borderLeftWidth: '3px', borderLeftColor: '#2A7B88' }}>
                <h4 className="text-sm font-bold mb-2" style={{ color: '#1B2A4A' }}>{flow.name}</h4>
                <ol className="space-y-1">
                  {flow.steps.map((step, si) => (
                    <li key={si} className="text-[12px]" style={{ color: '#6B7280' }}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </Section>

          {/* ROLES */}
          <Section id="roles" title="11. User Roles & Access">
            {(docs.roles || []).map((r, ri) => (
              <div key={ri} className="mb-3 p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2A7B88', color: 'white' }}>{r.role}</span>
                  <span className="text-[11px]" style={{ color: '#9CA3AF' }}>{r.users}</span>
                </div>
                <p className="text-[12px]" style={{ color: '#6B7280' }}>{r.access}</p>
              </div>
            ))}
          </Section>

          {/* USERS */}
          <Section id="users" title="12. System Users (Live)">
            <Table headers={['Name', 'Email', 'Role', 'Division', 'Scope']}
              rows={(live.users || []).map(u => [
                u.name || '-', u.email || '-', u.role || '-', u.division_id || 'all',
                u.sub_view ? JSON.stringify(u.sub_view).slice(0, 60) : u.site || '-'
              ])} />
          </Section>

          {/* IMPORT FORMATS */}
          <Section id="import-formats" title="13. Import Formats">
            {(docs.import_formats || []).map((fmt, fi) => (
              <div key={fi} className="mb-4 p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                <h4 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fmt.name}</h4>
                <p className="text-[12px] mt-1"><strong>Endpoint:</strong> <code>{fmt.endpoint}</code></p>
                <p className="text-[12px] mt-0.5"><strong>Columns:</strong> <code>{fmt.columns}</code></p>
                <ul className="mt-1.5 space-y-0.5">
                  {fmt.notes.map((n, ni) => (
                    <li key={ni} className="text-[11px]" style={{ color: '#6B7280' }}>- {n}</li>
                  ))}
                </ul>
              </div>
            ))}
          </Section>

          {/* ADMIN */}
          <Section id="admin" title="14. Admin Capabilities (No Code Changes)">
            <ul className="space-y-2">
              {(docs.admin_capabilities || []).map((cap, ci) => (
                <li key={ci} className="text-sm" style={{ color: '#1B2A4A' }} dangerouslySetInnerHTML={{ __html: cap.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </ul>
          </Section>

          {/* TRAINING ARCHITECTURE */}
          <TrainingArchitectureSection data={docs.training_architecture} />

          {/* INTEGRATIONS */}
          <Section id="integrations" title="15. Integrations (Planned)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { name: 'eZee Optimus (POS)', status: 'Pending API', desc: 'Auto-pull daily F&B revenue from 5 outlets' },
                { name: 'eZee Absolute (PMS)', status: 'Pending API', desc: 'Auto-pull room revenue & occupancy for Stays' },
                { name: 'Tally Prime', status: 'Phase 2C', desc: 'Auto-pull monthly P&L by cost centre' },
                { name: 'Gupshup WhatsApp', status: 'API Key Received', desc: 'Daily summaries, alerts, reminders via WhatsApp' },
                { name: 'Cloudinary', status: 'Pending Credentials', desc: 'Photo uploads for People profiles' },
                { name: 'Claude Sonnet 4.6', status: 'Active', desc: 'Mira AI assistant (via Emergent LLM Key)' },
              ].map((int, ii) => (
                <div key={ii} className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{int.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      int.status === 'Active' ? 'bg-emerald-50 text-emerald-700' :
                      int.status === 'API Key Received' ? 'bg-amber-50 text-amber-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>{int.status}</span>
                  </div>
                  <p className="text-[12px] mt-1" style={{ color: '#6B7280' }}>{int.desc}</p>
                </div>
              ))}
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t text-center print:hidden" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
            ScaleBoard System Documentation — Les Concierges Group — Auto-generated {new Date(docs.generated_at).toLocaleDateString('en-IN')}
          </p>
          <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
            Business manual by Dipali Sikand (CEO). Technical documentation auto-generated from live database.
          </p>
        </div>
      </main>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className="text-base font-bold pb-2 mb-4 border-b" style={{ color: '#1B2A4A', borderColor: '#2A7B88' }}>{title}</h2>
      <div className="text-sm" style={{ color: '#1B2A4A' }}>{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }) {
  return (
    <div id={id} className="mt-4 scroll-mt-20">
      <h3 className="text-sm font-semibold mb-2" style={{ color: '#2A7B88' }}>{title}</h3>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr style={{ backgroundColor: '#F7F5F2' }}>
            {headers.map((h, i) => (
              <th key={i} className="text-left px-3 py-2 font-semibold border-b" style={{ color: '#6B7280', borderColor: '#E5E1DC' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b" style={{ borderColor: '#F3F4F6' }}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2" style={{ color: '#1B2A4A' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function TrainingArchitectureSection({ data }) {
  if (!data) return null;
  const priorityColor = { P0: { bg: '#FEF2F2', fg: '#D4726A' }, P1: { bg: '#FEF3C7', fg: '#BA7517' }, P2: { bg: '#ECFDF5', fg: '#2D8B5E' } };
  return (
    <Section id="training-architecture" title="15. Unified Training System — Group-Wide">
      <p className="text-[12px] mb-4" style={{ color: '#6B7280' }}>{data.subtitle}</p>

      <SubSection id="training-summary" title="15.1 Summary">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC' }} data-testid="training-total">
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Total Planned</p>
            <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{data.summary?.total_modules_planned || 0}</p>
          </div>
          <div className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC', backgroundColor: '#ECFDF5' }} data-testid="training-live">
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Live</p>
            <p className="text-xl font-bold" style={{ color: '#2D8B5E' }}>{data.summary?.modules_live || 0}</p>
          </div>
          <div className="p-3 rounded-lg border" style={{ borderColor: '#E5E1DC', backgroundColor: '#FEF3C7' }} data-testid="training-todo">
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>To Build</p>
            <p className="text-xl font-bold" style={{ color: '#BA7517' }}>{data.summary?.modules_to_build || 0}</p>
          </div>
        </div>

        {(data.summary?.live_libraries || []).length > 0 && (
          <div className="mt-3 mb-3" data-testid="live-libraries">
            <p className="text-[12px] font-semibold mb-2" style={{ color: '#1B2A4A' }}>Live Libraries</p>
            <div className="space-y-2">
              {data.summary.live_libraries.map((lib, i) => (
                <div key={i} className="p-3 rounded-lg border flex flex-wrap items-start gap-3" style={{ borderColor: '#E5E1DC' }} data-testid={`live-library-${lib.code_prefix?.toLowerCase()}`}>
                  <div className="shrink-0">
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2A7B88', color: 'white' }}>{lib.code_prefix}</span>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[13px] font-semibold" style={{ color: '#1B2A4A' }}>{lib.division}</p>
                    <p className="text-[11px]" style={{ color: '#6B7280' }}>{lib.notes}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{lib.modules_live}</p>
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>modules · {lib.assignments}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px]" style={{ color: '#6B7280' }}>
          <strong>Priority Legend:</strong> P0 — within 30 days · P1 — within 60 days · P2 — within 90 days
        </p>
      </SubSection>

      <SubSection id="training-departments" title="15.2 MindEscapes Departmental Plan">
        <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
          The ME-specific 142-module master framework. LCG and TDB libraries (above) ship as complete wave-based libraries rather than department-indexed.
        </p>
        <div className="space-y-4">
          {(data.departments || []).map((dept, di) => {
            const pc = priorityColor[dept.priority] || priorityColor.P1;
            return (
              <div key={di} className="p-4 rounded-lg border" style={{ borderColor: '#E5E1DC' }} data-testid={`training-dept-${dept.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h4 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{dept.name}</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: pc.bg, color: pc.fg }}>{dept.priority}</span>
                  <span className="text-[10px]" style={{ color: '#6B7280' }}>Owner: {dept.owner}</span>
                  {dept.status && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>{dept.status}</span>
                  )}
                </div>
                <div className="mt-2 space-y-2">
                  {(dept.categories || []).map((cat, ci) => (
                    <div key={ci} className="pl-3 border-l-2" style={{ borderColor: '#2A7B88' }}>
                      <p className="text-[12px] font-semibold" style={{ color: '#2A7B88' }}>{cat.name}</p>
                      <ul className="mt-1 space-y-0.5">
                        {(cat.modules || []).map((mod, mi) => (
                          <li key={mi} className="text-[11px]" style={{ color: '#1B2A4A' }}>• {mod}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </SubSection>

      <SubSection id="training-implementation" title="15.3 Implementation Plan">
        <div className="space-y-2 mb-3">
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
            <p className="text-[12px]"><strong style={{ color: '#D4726A' }}>Phase 1 — </strong>{data.implementation_plan?.phase_1}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
            <p className="text-[12px]"><strong style={{ color: '#BA7517' }}>Phase 2 — </strong>{data.implementation_plan?.phase_2}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#ECFDF5' }}>
            <p className="text-[12px]"><strong style={{ color: '#2D8B5E' }}>Phase 3 — </strong>{data.implementation_plan?.phase_3}</p>
          </div>
        </div>
        <p className="text-[12px] font-semibold mb-2" style={{ color: '#1B2A4A' }}>Module Authoring Standards</p>
        <ul className="space-y-1">
          {(data.implementation_plan?.module_standards || []).map((s, i) => (
            <li key={i} className="text-[12px]" style={{ color: '#6B7280' }}>• {s}</li>
          ))}
        </ul>
      </SubSection>
    </Section>
  );
}
